import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as CCS2Icon } from '../../assets/icons/device-management/ccs2.svg';
// import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useAppBranding } from '../../config/branding';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { AvailableDevice } from '../../interface/device-management.interface';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import deviceApi from '../../utils/api/device';
import BatteryLoader from '../battery-loader/BatteryLoader';

import StationViewSidePanel from './StationViewSidePanel';

interface DeviceSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  currentData?: AvailableDevice;
}

interface Connector {
  connectorId: number;
  isReservationEnabled: boolean;
  status: string;
  _id: string;
  isAvailable: boolean;
}

interface ChargingPointProperties {
  _id?: string;
  chargingStation?: string;
  chargingPointName?: string;
  chargingPointSn?: string;
  maximumOutputPower?: string;
  maximumOutputPowerUnit?: string;
  connectorType?: string;
  isAvailable?: boolean;
  isDeviceOffline?: boolean;
  connectors: Connector[];
  chargePointModel?: string;
  chargePointVendor?: string;
  chargingStationName?: string;
  latitude?: string;
  longitude?: string;
  address?: string;
  accountCustomId?: string;
  accountName?: string;
  desiredPowerOutput?: number;
  powerUnit?: string;
  isJuiceBoxPncEnabled?: boolean;
  useTxDefaultProfile?: boolean;
  modelSeries?: string;
}

/**
 * Device side panel
 * @returns JSX.Element
 */
export default function DeviceViewSidePanel(properties: DeviceSidePanelProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('tableSidepanel');
  const { fetchIdFrom, id } = properties;
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const { isLoaded } = useGoogleMaps();
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const mapReference = useRef<google.maps.Map | null>(null);

  const [deviceDetails, setDeviceDetails] = useState<ChargingPointProperties>({
    connectors: [],
  });

  const [overlaySidePanel, setOverlaySidePanel] = useState<'closed' | 'station' | 'facility'>(
    'closed',
  );

  const [deviceImage, setDeviceImage] = useState<string>('');

  const deviceGeneralDetails = [
    {
      id: 1,
      label: t('stationName'),
      key: 'chargingStationName',
      /**
       * Open side panel for station view
       * @returns void
       */
      handleClick: () => setOverlaySidePanel('station'),
    },
    {
      id: 2,
      label: t('deviceId'),
      key: '_id',
    },
    {
      id: 3,
      label: t('accountName'),
      key: 'accountName',
      /**
       * Open side panel for station view
       * @returns
       */
      // handleClick: () => setOverlaySidePanel('facility'),
    },
    {
      id: 4,
      label: t('deviceModel'),
      key: 'chargePointModel',
    },
    {
      id: 5,
      label: t('deviceName'),
      key: 'chargingPointName',
    },
    {
      id: 6,
      label: t('deviceSerial'),
      key: 'chargingPointSn',
    },
    {
      id: 7,
      label: t('deviceVendor'),
      key: 'chargePointVendor',
    },
    {
      id: 8,
      label: t('firmwareVersion'),
      key: 'firmwareVersion',
    },
  ];

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchStationDetails = React.useCallback(
    async (stationId: string) => {
      setIsLoader(true);
      try {
        const response = await deviceApi.getChargingPointById(stationId);
        setDeviceDetails(response?.data);

        const model = response?.data?.chargePointModel;
        if (model) {
          const imageResponse = await deviceApi.fetchDeviceImage({
            chargePointVendor: response?.data?.chargePointVendor,
            chargePointModel: response?.data?.chargePointModel,
          });

          setDeviceImage(imageResponse?.data?.defaultMedias?.[0]?.url || '');
        }
      } catch (error: any) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      } finally {
        setIsLoader(false);
      }
    },
    [dispatch],
  );

  const validPosition = React.useMemo(() => {
    const lat = parseFloat(deviceDetails.latitude || '');
    const lng = parseFloat(deviceDetails.longitude || '');
    return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
  }, [deviceDetails.latitude, deviceDetails.longitude]);

  const stationId = React.useMemo(() => {
    return fetchIdFrom === 'queryParams' ? searchParameters.get('deviceId') : id;
  }, [fetchIdFrom, searchParameters, id]);

  useEffect(() => {
    if (!stationId) return;
    handleFetchStationDetails(stationId);
  }, [stationId, handleFetchStationDetails]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="device-wrap">
      <div className="side-header">
        <p className="title">{t('deviceDetails')}</p>
        <div className="action-buttons">{/* No actions for view only */}</div>
      </div>
      <div className="device-side-panel-container">
        <div className="device-preview">
          <div className="device-image-status-wrap">
            <div className="device-status-text-wrap">
              <div
                className={`device-status-text-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
                <div className="icon" />
                <p>{deviceDetails.isDeviceOffline ? t('offline') : t('online')}</p>
              </div>
            </div>
            {deviceImage.length ? (
              <img src={deviceImage} alt="" className="device-details-image" />
            ) : (
              <img
                src={branding.images.emptyChargerImage}
                alt=""
                className="device-details-image"
              />
            )}
            <div
              className={`device-status-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
              <div className="static-container" />
              <div className="animation-container" />
            </div>
          </div>
          <div className="map-address-container">
            <div className="device-side-panel-map">
              <h6>{t('deviceLocation')}</h6>
              <div className="map-container">
                {isLoaded && validPosition && (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={validPosition}
                    zoom={15}
                    onLoad={(map) => {
                      mapReference.current = map;
                    }}>
                    <Marker
                      position={validPosition}
                      icon={{
                        url: branding.icons.stationLocator,
                        scaledSize: new window.google.maps.Size(44, 44),
                      }}
                    />
                  </GoogleMap>
                )}
              </div>
            </div>
            <div className="address-wrap">
              <h6>{t('address')}</h6>
              <p>{deviceDetails?.address}</p>
            </div>
          </div>
        </div>
        <div className="content-wrap-with-scroll">
          {deviceDetails?.connectors && deviceDetails.connectors.length ? (
            <div className="connector-wrap">
              {deviceDetails.connectors.map((connector: any, index: number) => (
                <div className="each-connector" key={connector._id}>
                  <div className="connector-type">
                    {/* To-do -> Need to configure icon based on connector type */}
                    <span>
                      <CCS2Icon />
                    </span>
                    <h6>
                      {t('connectorLabel')} {connector.connectorId}
                    </h6>
                  </div>
                  <p>
                    {t('connector')} {connector.connectorId}
                  </p>
                  <div className={`connector-status ${connector.status}`}>{connector.status}</div>
                  {deviceDetails?.connectors && index !== deviceDetails.connectors.length - 1 && (
                    <div className="right-border" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <h6>{t('connectorUnavailable')}</h6>
          )}
        </div>
        <div className="device-side-panel-details">
          {deviceGeneralDetails.map((generalDetail) => (
            <div className="each-device-data" key={generalDetail.id}>
              <h6>{generalDetail.label}</h6>
              <p
                role="presentation"
                onClick={generalDetail.handleClick ? generalDetail.handleClick : () => {}}
                className={generalDetail.handleClick ? 'hyperlink' : ''}>
                {String(deviceDetails[generalDetail.key as keyof ChargingPointProperties] || '-')}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="device-side-panel-description" />
      <div
        className={`device-panel-station-view-wrap ${overlaySidePanel === 'station' || overlaySidePanel === 'facility' ? 'active' : ''}`}>
        <div className="side-panel-header">
          {/* Need to configure after new tab integration */}
          <span style={{ opacity: '0.5', cursor: 'not-allowed' }}>{/* <NewTabIcon /> */}</span>
          <span role="presentation" onClick={() => setOverlaySidePanel('closed')}>
            <CloseIcon />
          </span>
        </div>
        {overlaySidePanel === 'station' ? (
          <div className="side-panel-content">
            <StationViewSidePanel fetchIdFrom="props" id={deviceDetails.chargingStation} />
          </div>
        ) : (
          <div className="side-panel-content">
            {/* <FacilitySidePanel fetchIdFrom="props" id={deviceDetails.accountCustomId} /> */}
            <div />
          </div>
        )}
      </div>
    </div>
  );
}
